// React
import React, { FC, useMemo, useEffect, useState, useContext } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Styles
import injectSheet from 'react-jss';

// Components
import {
  GoodDataDashboard,
  GoodDataDropdown,
  GoodDataIncompatibleBrowserModal,
} from 'Containers/GoodData/components';
import DownloadInquiries from 'Containers/DownloadInquiries';

// Utilities
import {
  eventListenerGoodData,
  constructTargetUrl,
  getDashboardConfiguration,
  getSsoConfiguration,
  isUserOnOrPastSafariTwelve,
} from 'Containers/GoodData/utilities';
import { areEqual } from 'Utils/areEqual';
import { EngagementDefaultDates } from 'Components/RangeDatePicker';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import withErrorBoundary from 'HOC/ErrorBoundary';
import stylesheet from './styles';
import {
  setEventListenerHasBeenAttachedValue,
  setGoodDataEventValue,
  setPlatformInfoEventValue,
  setFilterValueChangeEventValue,
  setUIDrillEventValue,
  setUserBrowserValue,
} from '../actions/goodData.actions';

interface Props {
  classes?: any;

  // The name of this dashboard as its known on the back end.
  title: string;
  name: string;
  id: string;

  // The GraphQL data obtained from the User.GoodDataClaims Query.
  gdData: any;

  // Styling of the iframe.  Shouldnt need any more than these.
  width: string;
  height: string;
  border: string;
  marginLeft?: string;
  moduleAccess: any;
  errorContainerStyles?: {
    width: string;
    height: string;
  };

  // The ssoCallback and dashboardCallback are for getting the unwrapped and
  // constructed items back, in case you need their ID's or login info.
  ssoCallback?: (ssoConfiguration: any) => void;
  dashboardCallback?: (dashboardConfiguration: any) => void;

  // Called when the dropdown is changed, if it is active.  Usually wanna
  // pass a modifyGoodDataFilterContext function into this.
  handleDropdownChange?: (event: any) => void;
  handleCalendarChange?: any;
  startDate?: string;
  endDate?: string;
  selectedOption?: { label: string; value: number };

  // Determines if the dropdown is used at all
  useGoodDataDropdown?: boolean;

  // Uses this as the styling for the dropdown, so that you can absolutely
  // position it as require, since these dropdowns must exist and overlay
  // the iframes to block input.
  goodDataDropdownStyling?: object;

  // Pass this to lock the iframe from scrolling.
  lockScrolling?: boolean;

  // This is for Combined Analytics
  dashBoardName?: string;
}

const GoodDataDashboardContainer: FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const { eventListenerHasBeenAttached, userBrowser } = useSelector(
    (selectors: any) => selectors.goodData,
    areEqual
  );

  // These two states are used to compare and limit re-renders
  const [ssoState, setSsoState] = useState(undefined);
  const [dashboardState, setDashboardState] = useState(undefined);
  const {
    actions: { track },
  } = useContext(ProductAnalyticsContext);

  const {
    classes,
    title,
    name,
    id,
    gdData,
    width,
    height,
    border,
    marginLeft,
    errorContainerStyles,
    ssoCallback,
    dashboardCallback,
    handleDropdownChange,
    handleCalendarChange,
    startDate,
    endDate,
    selectedOption,
    useGoodDataDropdown,
    goodDataDropdownStyling,
    lockScrolling,
    dashBoardName,
    moduleAccess,
  } = props;

  useEffect(() => {
    if (!userBrowser) {
      setUserBrowserValue(dispatch);
    }
  }, []);

  useEffect(() => {
    // Combined Analytics event
    if (dashBoardName === 'VT' || dashBoardName === 'IWC') {
      track(
        `${
          name === 'analytics_cp_iwc_details'
            ? 'content_performance_iwc_details_viewed'
            : 'content_performance_vt_details_viewed'
        }`
      );
    }
  }, []);

  if (!eventListenerHasBeenAttached) {
    setEventListenerHasBeenAttachedValue(dispatch, true);
    eventListenerGoodData((type: string, event: any) => {
      setGoodDataEventValue(dispatch, {
        type,
        event,
      });

      switch (type) {
        case 'platform.info':
          setPlatformInfoEventValue(dispatch, true);
          break;
        case 'ui.frameinfo':
          // Set PlatformInfoEvent again on getting `ui.frameinfo` message, because
          // sometimes message sent after getting `platform.info` is missed by Good Data
          // (related bug AC-721: Incorrect location opens when clicked on a location in "Analytics > Content Performance > Virtual Tours").
          //
          // Adding another case for `ui.frameinfo` instead of only using `platform.info`, because
          // sending message on `platform.info` makes UI more responsive when it works.
          // For AC-721, Location image will always takes a few seconds to change to correct image, if we only act on ui.frameinfo.
          setPlatformInfoEventValue(dispatch, true);
          break;
        case 'filter.value.changed':
          setFilterValueChangeEventValue(dispatch, true);

          break;
        case 'ui.drill':
          setUIDrillEventValue(dispatch, true);
          break;
        default:
          break;
      }
    });
  }

  // Unwrap and construct the ssoConfiguration.  useEffect is used to check
  // and compare the newly constructed object to state.  If it is different,
  // it will update state and make the ssoCallback, giving you up-to-date sso data,
  // if you have provided it.
  const ssoConfiguration = getSsoConfiguration(gdData);
  useEffect(() => {
    if (ssoConfiguration && !areEqual(ssoState, ssoConfiguration)) {
      setSsoState(ssoConfiguration);
      if (ssoCallback) {
        ssoCallback(ssoConfiguration);
      }
    }
  }, [ssoConfiguration]);

  // Unwrap and construct the dashboardConfiguration.  useEffect is used to check
  // and compare the newly constructed object to state.  If it is different,
  // it will update state and make the dashboardCallback, giving you up-to-date dashboard data,
  // if you have provided it.
  const dashboardConfiguration = getDashboardConfiguration(name, gdData);
  useEffect(() => {
    if (
      dashboardConfiguration &&
      !areEqual(dashboardState, dashboardConfiguration)
    ) {
      setDashboardState(dashboardConfiguration);
      if (dashboardCallback) {
        dashboardCallback(dashboardConfiguration);
      }
    }
  }, [dashboardConfiguration]);

  // Calls this function every time dropdown changes, when it is active.  Will
  // check to see if you provided a custom handler before attempting to call.
  const onDropdownChange = (event: any) => {
    if (handleDropdownChange) {
      handleDropdownChange(event);
    }
  };

  // Memoization function returning JSX.  Needs to be called.
  // Will re-run anytime dashboardState or ssoState are changed.
  // Limits re-renders and ensures the iframe will not get stuck in
  // a refresh loop (a very real problem should you delete this.)
  const memoizeDashboard = (dashboard: any) =>
    useMemo(() => {
      return (
        <GoodDataDashboard
          title={title}
          name={name}
          id={id}
          loginUrl={ssoState && ssoState.loginUrl}
          targetUrl={constructTargetUrl(
            dashboard && dashboard.project,
            dashboard && dashboard.dashboard,
            true,
            true,
            true,
            false
          )}
          ssoProvider={ssoState && ssoState.ssoProvider}
          encryptedClaims={ssoState && ssoState.encryptedClaims}
          submit={(ref) => {
            ref.current.submit();
          }}
          overrideStyles={{
            width,
            height,
            border,
            marginLeft,
          }}
          lockScrolling={lockScrolling}
        />
      );
    }, [dashboardState, ssoState]);

  // Constructing the memoized dashboard
  const memoizedDashboard = memoizeDashboard(
    dashboardState && dashboardState.dashboard
  );

  const render = () => {
    let jsx = null;

    if (
      dashboardState &&
      dashboardState.enabled &&
      userBrowser &&
      !isUserOnOrPastSafariTwelve(userBrowser)
    ) {
      jsx = (
        <>
          <div style={goodDataDropdownStyling}>
            {moduleAccess &&
              moduleAccess.AAP_ANALYTICS_CONTENT_PERFORMANCE_DOWNLOAD_INQUIRIES && (
                <DownloadInquiries
                  startDate={startDate}
                  endDate={endDate}
                  locations={gdData.institution.locations}
                />
              )}
            {useGoodDataDropdown && (
              <GoodDataDropdown
                handleDropdownChange={onDropdownChange}
                startDate={startDate}
                endDate={endDate}
                selectedOption={selectedOption}
                handleCalendarChange={handleCalendarChange}
                dashBoardName={dashBoardName}
              />
            )}
          </div>
          {memoizedDashboard}
        </>
      );
    } else if (userBrowser && isUserOnOrPastSafariTwelve(userBrowser)) {
      jsx = (
        <div className={classes.sadContainer} style={errorContainerStyles}>
          <GoodDataIncompatibleBrowserModal />
        </div>
      );
    }

    return jsx;
  };

  // Will return the dashboard and dropdown if everything is available, or null
  // if it is not.
  return render();
};

GoodDataDashboardContainer.defaultProps = {
  classes: {},
  marginLeft: '0px',
  errorContainerStyles: { width: '0px', height: '0px' },
  ssoCallback: () => {},
  dashboardCallback: () => {},
  handleDropdownChange: () => {},
  handleCalendarChange: () => {},
  startDate: EngagementDefaultDates.minDate,
  endDate: EngagementDefaultDates.maxDate,
  selectedOption: {
    label: 'Last 30 days',
    value: -31,
  },
  useGoodDataDropdown: false,
  goodDataDropdownStyling: {},
  lockScrolling: false,
  dashBoardName: '',
};

export default withErrorBoundary(
  injectSheet(stylesheet)(GoodDataDashboardContainer)
);
